<template>
  <PortalWidget :widget="widget">
    <template v-slot:headerContent>
      <div class="header-extra">
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('API_TOKENS_SEARCH_PH')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div
      v-if="!isFetchingTokens"
      class="m-t-10 m-b-15"
    >
      <button
        type="button"
        class="btn btn-success btn-sm m-r-5"
        :disabled="showCreateForm"
        @click="onShowCreateForm"
      >
        <i class="fa fa-plus" />
        <span v-t="'API_TOKENS_ADD_BUTTON'" />
      </button>
    </div>
    <!-- Table Container -->
    <div
      v-if="!isFetchingTokens"
      class="grid_table_holder"
    >
      <v-table
        :data="apiTokensList"
        :current-page.sync="currentPage"
        :filters="filters"
        :page-size="20"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />

            <v-th
              default-sort="asc"
              sort-key="name"
              v-t="'API_TOKENS_NAME'"
            />
            <v-th
              sort-key="usage[0].loginTime"
              v-t="'API_TOKENS_LAST_DATE'"
            />
            <v-th
              sort-key="usage[0].protectedResource"
              v-t="'API_TOKENS_LAST_RESOURCE'"
            />
            <v-th
              sort-key="usage[0].ipAddress"
              v-t="'API_TOKENS_LAST_IP'"
            />
            <th
              v-if="!isMobile"
              v-t="'TABLE_COL_ACTIONS'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >

          <tr v-if="!showCreateForm && !apiTokensList.length">
            <td colspan="100" v-t="'API_TOKENS_HEADER_NONE'"></td>
          </tr>
          <!-- Create New Form -->
          <tr
            v-if="showCreateForm"
            class="row-inline-new-form slide-down bg-row-even"
          >
            <td colspan="100">
              <ApiTokenForm
                @create-api-token="onOpenCreateAuthModal"
                @close-api-token-form="hideCreateForm"
              />
            </td>
          </tr>

          <!-- Rows -->
          <template v-for="(row, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == row.tokenId)"
              :key="row.tokenId"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0,
              }"
            >
              <!-- Mobile Actions -->
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  @click="displayMobileAction(row.tokenId)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>
              <td>
                {{ row.name }}
              </td>
              <td>
                {{ row.usage[0] && row.usage[0].loginTime ? row.usage[0].loginTime : '-' }}
              </td>
              <td>
                {{ row.usage[0] && row.usage[0].protectedResource ? row.usage[0].protectedResource : '-' }}
              </td>
              <td>
                {{ row.usage[0] && row.usage[0].ipAddress ? row.usage[0].ipAddress : '-' }}
              </td>

              <!-- Desktop Actions -->
              <td v-if="!isMobile">
                <ApiTokenActions
                  :current-api-token="row"
                  :disabled="disabled"
                  @deleteApiToken="onShowDeleteApiTokenModal"
                  @showApiTokenUsage="onShowUsage(row.tokenId)"
                />
              </td>
            </tr>

            <tr
              v-if="isMobile && activeActionRowId == row.tokenId"
              :key="row.tokenId + '-mobile'"
              class="mobile-actions slide-right"
            >
              <td colspan="10">
                <ApiTokenActions
                  :current-api-token="row"
                  :disabled="disabled"
                  @collapseActions="!disabled && hideMobileAction()"
                  @deleteApiToken="onShowDeleteApiTokenModal"
                  @showApiTokenUsage="onShowUsage(row.tokenId)"
                />
              </td>
            </tr>
            <!-- Usage -->
            <tr
              v-if="showUsage(row.tokenId)"
              :key="row.tokenId + '-usage'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <ApiTokenUsage
                  :usage="row.usage"
                  @hideUsage="hideUsage"
                />
              </td>
            </tr>
            <!-- Usage -->
          </template>
        </tbody>
      </v-table>
      <div v-if="apiTokensList && apiTokensList.length" class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="7"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'API_TOKENS_HEADER_LOADING'" />
    </div>
    <ApiTokenDeleteModal
      v-if="showDeleteModal"
      :api-token="apiTokenToBeDeleted"
      :is-loading="isDeletingToken"
      @confirm-token-modal="onOpenDeleteAuthModal"
      @close-delete-token-modal="onCloseDeleteModal"
    />
    <MultiFactorAuthModal
      v-if="showMFAModal"
      :model="mfaModel"
      :is-loading="isDeletingToken || isCreatingToken"
      @confirm-modal="onConfirmMFAModal"
      @close-modal="onCancelAuthModal"
    />
  </PortalWidget>
</template>

<script>
import { cacheService, utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import { isMobile } from '@/core/utilities';
import PortalWidget from '@/core/components/PortalWidget.vue';
import Loader from '@/core/components/Loader';
import ApiTokenActions from './ApiTokenActions';
import ApiTokenForm from './ApiTokenForm';
import ApiTokenUsage from './ApiTokenUsage';
import ApiTokenDeleteModal from './ApiTokenForm/ApiTokenDeleteModal';
import MultiFactorAuthModal from '@/modules/auth/components/MultiFactorAuthModal';

import { mapActions, mapState, mapGetters } from 'vuex';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';

const two_factor_errors = [
  'two_auth_sent',
  'two_auth_required'
];

export default {
  components: {
    PortalWidget,
    Loader,
    ApiTokenActions,
    ApiTokenUsage,
    ApiTokenForm,
    ApiTokenDeleteModal,
    MultiFactorAuthModal
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      isMobile: isMobile,
      activeActionRowId: null,
      activeUsageRowId: null,
      showCreateForm: false,
      showDeleteModal: false,
      showMFAModal: false,
      mfaModel: null,
      apiTokenToBeDeleted: {},
      widget: {
        title:this.$t('API_TOKENS_MANAGE'),
        iconClass: 'typcn typcn-key-outline'
      },
      filters: {
        name: { value: '', keys: ['name'] }
      },
    }
  },
  watch: {
    currentPage() {
      this.showCreateForm = false;
    },
    filters: {
      immediate: true,
      deep: true,
      handler() {
        this.showCreateForm = false;
      }
    }
  },
  created() {
    this.getApiTokensList();
  },
  computed: {
    ...mapState('apiTokens', ['apiTokensList']),
    ...mapGetters('apiTokens', [
      'getApiTokensListStatus',
      'deleteApiTokenStatus',
      'createApiTokenStatus'
    ]),
    isFetchingTokens() {
      return this.getApiTokensListStatus.status == STATUS_PENDING;
    },
    isDeletingToken() {
      return this.deleteApiTokenStatus.status == STATUS_PENDING;
    },
    isCreatingToken() {
      return this.createApiTokenStatus.status == STATUS_PENDING;
    },
    disabled() {
      return (
        this.createApiTokenStatus.status == STATUS_PENDING
        || this.showCreateForm
        || !!this.activeUsageRowId
      );
    },
  },
  methods: {
    ...mapActions('apiTokens', [
      'getApiTokensList',
      'deleteApiToken',
      'createApiToken'
    ]),
    onShowDeleteApiTokenModal(apiToken) {
      this.apiTokenToBeDeleted = apiToken;
      this.showDeleteModal = true;
    },
    displayMobileAction(id) {
      this.activeActionRowId = id;
    },
    hideMobileAction() {
      this.activeActionRowId = null;
    },
    onShowCreateForm() {
      this.showCreateForm = true;
    },
    onShowUsage(id) {
      this.hideCreateForm();
      this.activeUsageRowId = id;
    },
    showUsage(id) {
      return !this.showCreateForm && this.activeUsageRowId == id;
    },
    hideCreateForm() {
      this.showCreateForm = false;
    },
    hideUsage() {
      this.activeUsageRowId = null;
    },
    onCloseDeleteModal() {
      this.apiTokenToBeDeleted = {};
      this.showDeleteModal = false;
    },
    onCancelAuthModal() {
      this.apiTokenToBeDeleted = {};
      this.showMFAModal = false;
    },
    onOpenDeleteAuthModal() {
      if (cacheService.get('apitoken.userAuthenticated')) {
        this.onDeleteApiToken({deleteModel:null});
      } else {
        this.onConfirmMFAModal = this.onDeleteApiToken;
        this.showDeleteModal = false;
        this.showMFAModal = true;
      }
    },
    async onDeleteApiToken(deleteModel) {
      const payload = {
        tokenId: this.apiTokenToBeDeleted.tokenId,
        multiFactorAuthCode: deleteModel.multiFactorAuthCode,
        email: deleteModel.email == '' ? null : deleteModel.email,
        password: deleteModel.password
      };

      try {
        await this.deleteApiToken(payload);

        if (this.deleteApiTokenStatus.status === STATUS_SUCCESS) {
          this.getApiTokensList();
          this._setAuthenticated();
          this.onCancelAuthModal();
          this.onCloseDeleteModal();
          utilities.showToaster(STATUS_SUCCESS, this.$t('API_TOKEN_DELETE_MESSAGE'));
        }
      } catch (error) {
        this._validateError(error, this.onDeleteApiToken);
      }
    },
    onOpenCreateAuthModal(createModel) {
      if (cacheService.get('apitoken.userAuthenticated')) {
        this.onCreateApiToken({model:createModel});
      } else {
        this.onConfirmMFAModal = this.onCreateApiToken;
        this.showDeleteModal = false;
        this.showMFAModal = true;
        this.mfaModel = createModel;
      }
    },
    async onCreateApiToken(createModel) {
      const payload = {
        name: createModel.model.name,
        multiFactorAuthCode: createModel.multiFactorAuthCode,
        email: createModel.email == '' ? null : createModel.email,
        password: createModel.password
      };

      try {
        await this.createApiToken(payload);

        if (this.createApiTokenStatus.status === STATUS_SUCCESS) {
          this.getApiTokensList();
          this._setAuthenticated();
          this.onCancelAuthModal();
          utilities.showToaster(STATUS_SUCCESS, this.$t('API_TOKEN_CREATE_SUCCESS'));
        }
      } catch (error) {
        this._validateError(error, this.onCreateApiToken, createModel.model);
      }
    },
    _validateError(error, confirmMFA, model) {
      if (two_factor_errors.includes(error.data.error) || error.data.error == 'required_second_auth') {
        if (this.showMFAModal) {
          utilities.showToaster('warning', this.$t(error.data.message));
        }
        this.mfaModel = model;
        this.onConfirmMFAModal = confirmMFA;
      } else if (error.data.errors && error.data.errors[0].errors) {
        utilities.showToaster(STATUS_ERROR, error.data.errors[0].errors[0]);
      }
      else {
        utilities.errorHandler(error);
      }
    },
    _setAuthenticated() {
      cacheService.set('apitoken.userAuthenticated', true, {
        expireAfterSeconds: 280 // 4:40m to avoid passing here, and not in the backend
      });
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>
